// Comentário forçado para limpar cache da Vercel e garantir deploy novo - 2026-02-13
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AuthProvider } from "./components/AuthProvider";
import { SyncProvider } from "./contexts/SyncContext";
import { InternationalizationProvider } from "./contexts/InternationalizationContext";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Páginas públicas
import Login from "./pages/Login";
import Cadastro from "./pages/Cadastro";
import ResetPassword from "./pages/ResetPassword";
import ConfirmarEmail from "./pages/ConfirmarEmail";
import OrcamentoPublico from "./pages/OrcamentoPublico";
import Termos from "./pages/termos";
import Privacidade from "./pages/privacidade";

// Páginas protegidas
import Dashboard from "./pages/Dashboard";
import Pedidos from "./pages/Pedidos";
import PDV from "./pages/PDV";
import NovoPedido from "./pages/NovoPedido";
import EditarPedido from "./pages/EditarPedido";
import PedidoDetalhe from "./pages/PedidoDetalhe";
import Orcamentos from "./pages/Orcamentos";
import NovoOrcamento from "./pages/NovoOrcamento";
import EditarOrcamento from "./pages/EditarOrcamento";
import OrcamentoImpressaoNovo from "./pages/OrcamentoImpressaoNovo";
import PedidosImpressao from "./pages/PedidosImpressao";
import PedidoCompartilhado from "./pages/PedidoCompartilhado";
import VisualizarOrcamento from "./pages/VisualizarOrcamento";
import Clientes from "./pages/Clientes";
import Estoque from "./pages/Estoque";
import DuplicadosEstoque from "./pages/DuplicadosEstoque";
import CatalogoProdutos from "./pages/CatalogoProdutos";
import CalculadoraPrecos from "./pages/CalculadoraPrecos";
import Relatorios from "./pages/Relatorios";
import ControleFinanceiro from "./pages/ControleFinanceiro";
import Assinatura from "./pages/assinatura";
import AssinaturaSucesso from "./pages/AssinaturaSucesso";
import VerificarPagamento from "./pages/VerificarPagamento";
import MinhaConta from "./pages/MinhaConta";
import Agenda from "./pages/Agenda";
import OrdemProducao from "./pages/OrdemProducao";
import MedidasClientes from "./pages/MedidasClientes";
import TabelaMedidas from "./pages/TabelaMedidas";
import Ajuda from "./pages/Ajuda";
import AjudaNotasFiscais from "./pages/AjudaNotasFiscais";
import Documentacao from "./pages/Documentacao";
import FAQ from "./pages/FAQ";
import RelatorioUso from "./pages/RelatorioUso";
import RelatorioConsertos from "./pages/RelatorioConsertos";
import AdminErros from "./pages/AdminErros";
import AdminPanel from "./pages/AdminPanel";
import ConfiguracaoFocusNF from "./pages/ConfiguracaoFocusNF";
import GestaoNotasFiscais from "./pages/GestaoNotasFiscais";
import Fornecedores from "./pages/Fornecedores";
import ContasPagar from "./pages/ContasPagar";
import ContasReceber from "./pages/ContasReceber";
import PedidosCompra from "./pages/PedidosCompra";
import MovimentacoesEstoque from "./pages/MovimentacoesEstoque";
import FluxoCaixa from "./pages/FluxoCaixa";
import ConfiguracaoParcelamentos from "./pages/ConfiguracaoParcelamentos";
import Indicacoes from "./pages/Indicacoes";
import Recompensas from "./pages/Recompensas";
import ConfiguracaoWhatsApp from "./pages/ConfiguracaoWhatsApp";
import AdminComissoes from "./pages/AdminComissoes";
import Servicos from "./pages/Servicos";
import PreviewSubscriptionExpired from "./pages/PreviewSubscriptionExpired";
import { Configuracoes } from "./pages/Configuracoes";
import { Empresa } from "./pages/Empresa";
import { UsuariosPermissoes } from "./pages/UsuariosPermissoes";
import { RegistroAtividades } from "./pages/RegistroAtividades";
import { ConfiguracaoPermissoes } from "./pages/ConfiguracaoPermissoes";
import { ConfiguracaoWhatsAppTemplates } from "./pages/ConfiguracaoWhatsAppTemplates";
import { AdminDashboard } from "./pages/admin/dashboard";
import { ConfiguracaoTimeline } from "./pages/ConfiguracaoTimeline";
import ConfiguracaoTiposPeca from "./pages/ConfiguracaoTiposPeca";

// QueryClient fora do componente para evitar re-criação
// Cache buster: 2024-12-19 - Corrigindo Dashboard not defined
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // Cache por 30 segundos (dados considerados frescos)
      gcTime: 5 * 60 * 1000, // Manter cache por 5 minutos
      retry: 2, // Tentar 2 vezes em caso de erro (reduzido de 3)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
      refetchOnWindowFocus: false, // Não refetch ao focar na janela
      refetchOnReconnect: true, // Refetch ao reconectar
      refetchOnMount: false, // Não refetch ao montar componente se dados estão frescos
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
              <InternationalizationProvider>
                <SyncProvider>
                  <BrowserRouter>
                  <Routes>
                  {/* Rotas públicas */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/cadastro" element={<Cadastro />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/confirmar-email" element={<ConfirmarEmail />} />
                  <Route path="/orcamento/:id" element={<OrcamentoPublico />} />
                  <Route path="/orcamentos/:id/impressao" element={<OrcamentoImpressaoNovo />} />
                  <Route path="/pedido/compartilhado/:codigo" element={<PedidoCompartilhado />} />
                  <Route path="/assinatura-sucesso" element={<AssinaturaSucesso />} />
                  <Route path="/verificar-pagamento" element={<VerificarPagamento />} />
                  <Route path="/termos" element={<Termos />} />
                  <Route path="/privacidade" element={<Privacidade />} />
                  
                  {/* Rotas protegidas com Layout */}
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="pedidos" element={<Pedidos />} />
                    <Route path="pdv" element={<PDV />} />
                    <Route path="pedidos/novo" element={<NovoPedido />} />
                    <Route path="pedidos/editar/:id" element={<EditarPedido />} />
                    <Route path="pedidos/:id/producao" element={<OrdemProducao />} />
                    <Route path="pedidos/:id/impressao" element={<PedidosImpressao />} />
                    <Route path="pedidos/:id" element={<PedidoDetalhe />} />
                    <Route path="orcamentos" element={<Orcamentos />} />
                    <Route path="orcamentos/novo" element={<NovoOrcamento />} />
                    <Route path="orcamentos/editar/:id" element={<EditarOrcamento />} />
                    <Route path="orcamentos/:id/visualizar" element={<VisualizarOrcamento />} />
                    <Route path="clientes" element={<Clientes />} />
                    <Route path="estoque" element={<Estoque />} />
                    <Route path="estoque/duplicados" element={<DuplicadosEstoque />} />
                    <Route path="catalogo" element={<CatalogoProdutos />} />
                    <Route path="calculadora" element={<CalculadoraPrecos />} />
                    <Route path="relatorios" element={<Relatorios />} />
                    <Route path="relatorio-consertos" element={<RelatorioConsertos />} />
                    <Route path="financeiro" element={<ControleFinanceiro />} />
                    <Route path="assinatura" element={<Assinatura />} />
                    <Route path="indicacoes" element={<Indicacoes />} />
                    <Route path="recompensas" element={<Recompensas />} />
                    <Route path="configuracao-whatsapp" element={<ConfiguracaoWhatsApp />} />
                    <Route path="minha-conta" element={<MinhaConta />} />
                    <Route path="admin/comissoes" element={<AdminComissoes />} />
                    <Route path="agenda" element={<Agenda />} />
                    <Route path="medidas" element={<MedidasClientes />} />
                    <Route path="tabela-medidas" element={<TabelaMedidas />} />
                    <Route path="configuracao-tipos-peca" element={<ConfiguracaoTiposPeca />} />
                    <Route path="ajuda" element={<Ajuda />} />
                    <Route path="ajuda-notas-fiscais" element={<AjudaNotasFiscais />} />
                    <Route path="documentacao" element={<Documentacao />} />
                    <Route path="faq" element={<FAQ />} />
                    <Route path="admin/dashboard" element={<AdminDashboard />} />
                    <Route path="admin/relatorio-uso" element={<RelatorioUso />} />
                    <Route path="admin/erros" element={<AdminErros />} />
                    <Route path="admin/panel" element={<AdminPanel />} />
                    <Route path="notas-fiscais" element={<GestaoNotasFiscais />} />
                    <Route path="configuracao-focusnf" element={<ConfiguracaoFocusNF />} />
                    <Route path="fornecedores" element={<Fornecedores />} />
                    <Route path="contas-pagar" element={<ContasPagar />} />
                    <Route path="contas-receber" element={<ContasReceber />} />
                    <Route path="pedidos-compra" element={<PedidosCompra />} />
                    <Route path="movimentacoes-estoque" element={<MovimentacoesEstoque />} />
                    <Route path="fluxo-caixa" element={<FluxoCaixa />} />
                    <Route path="fluxo-caixa/configuracao-parcelamentos" element={<ConfiguracaoParcelamentos />} />
                    <Route path="servicos" element={<Servicos />} />
                    <Route path="configuracoes" element={<Configuracoes />} />
                    <Route path="configuracoes/empresa" element={<Empresa />} />
                    <Route path="configuracoes/usuarios" element={<UsuariosPermissoes />} />
                    <Route path="configuracoes/registro-atividades" element={<RegistroAtividades />} />
                    <Route path="configuracoes/permissoes" element={<ConfiguracaoPermissoes />} />
                    <Route path="configuracoes/whatsapp" element={<ConfiguracaoWhatsAppTemplates />} />
                    <Route path="configuracoes/timeline" element={<ConfiguracaoTimeline />} />
                  </Route>

                  {/* Preview routes - apenas em desenvolvimento */}
                  {import.meta.env.DEV && (
                    <>
                      <Route path="/preview/subscription-expired" element={<PreviewSubscriptionExpired />} />
                    </>
                  )}
                </Routes>
                  </BrowserRouter>
                </SyncProvider>
              </InternationalizationProvider>
            </AuthProvider>
          </TooltipProvider>
          <Sonner />
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;